// Lightweight client diagnostics for sign-in / registration issues.
// No deps. Used by LoginPage & RegisterPage to surface real root causes
// instead of generic "please try again" messages.

// Detects whether the browser will accept cookies written by the same-origin API.
// Returns { ok: bool, reason: string | null }.
export const checkCookieSupport = () => {
  if (typeof document === 'undefined') return { ok: true, reason: null };

  // navigator.cookieEnabled is the coarse signal; works on all modern browsers.
  if (navigator.cookieEnabled === false) {
    return { ok: false, reason: 'cookies_disabled' };
  }

  // Try to write & read a probe cookie. Fails silently on strict Safari private mode.
  try {
    const key = '__ascend_probe__';
    document.cookie = `${key}=1; path=/; SameSite=Lax`;
    const present = document.cookie.includes(`${key}=1`);
    // Cleanup
    document.cookie = `${key}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    if (!present) return { ok: false, reason: 'cookies_blocked' };
  } catch {
    return { ok: false, reason: 'cookies_error' };
  }

  return { ok: true, reason: null };
};

// Maps machine reasons to friendly messages shown to the user.
export const AUTH_ERROR_MESSAGES = {
  cookies_disabled: "Your browser has cookies turned off. Please enable cookies in Settings and try again — we need them to keep you signed in.",
  cookies_blocked: "Your browser is blocking cookies. On iPhone, go to Settings → Safari → turn OFF 'Block All Cookies' and turn OFF 'Prevent Cross-Site Tracking' (just for ascendforyou.net), then try again.",
  cookies_error: "We couldn't set a secure session cookie. Try disabling private/incognito mode or switching browsers.",
  session_expired: "Your sign-in session expired before we could finish. Please click 'Continue with Google' again.",
  network: "We couldn't reach our servers. Check your internet connection and try again.",
  auth_failed: "Google sign-in didn't complete. Please try again — or sign up with email and password below.",
  server_error: "Something went wrong on our end. Please try again in a moment.",
};

// Inspects an Axios/network error and returns a machine-readable reason code.
export const classifyAuthError = (err) => {
  if (!err) return 'server_error';
  // Network / offline
  if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') return 'network';
  const status = err.response?.status;
  if (status === 401) return 'session_expired';
  if (status >= 500) return 'server_error';
  return null; // let caller fall back to detail string
};
